/**
 * Shared utilities for the summarization UI.
 *
 * The `StatusDot` / `SeverityBadge` components used to live here too, but
 * react-refresh/only-export-components wants a file to export either
 * components or values, never both — they now live in `./StatusBadges`.
 */

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDuration(ms: number): string {
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  return `${mins}m ${remainSecs}s`;
}

export function formatCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${value < 0 ? "-" : ""}$${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${value < 0 ? "-" : ""}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `$${value.toLocaleString()}`;
}

export function formatMetricName(name: string): string {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/\b(Ebitda|Ebit)\b/gi, (s) => s.toUpperCase())
    .trim();
}

export function tryFormatJson(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

// ---------------------------------------------------------------------------
// Agent text parser — splits agent stream text into structured segments
// ---------------------------------------------------------------------------

export type TextSegmentType =
  | { type: "heading"; level: number; text: string }
  | { type: "prose"; text: string }
  | { type: "json_output"; json: string }
  | { type: "list_item"; text: string }
  | { type: "phase_marker"; phase: string };

export function parseAgentText(text: string): TextSegmentType[] {
  const segments: TextSegmentType[] = [];
  const lines = text.split("\n");
  let i = 0;
  let proseBuffer: string[] = [];

  const flushProse = () => {
    const joined = proseBuffer.join("\n").trim();
    if (joined.length > 0) {
      segments.push({ type: "prose", text: joined });
    }
    proseBuffer = [];
  };

  while (i < lines.length) {
    const line = lines[i];

    // ```json blocks → collapse into a single "output generated" segment
    if (line.trim().startsWith("```json")) {
      flushProse();
      const jsonLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        jsonLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;
      segments.push({ type: "json_output", json: jsonLines.join("\n") });
      continue;
    }

    // Other code blocks — just skip the fences
    if (line.trim().startsWith("```")) {
      flushProse();
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        proseBuffer.push(lines[i]);
        i++;
      }
      flushProse();
      if (i < lines.length) i++;
      continue;
    }

    // Phase markers like "### Phase 1: Orientation"
    const phaseMatch = line.match(
      /^#{1,4}\s*(?:Phase|PHASE|Step|STEP)\s*(\d+)[:\s—-]*(.*)/i,
    );
    if (phaseMatch) {
      flushProse();
      segments.push({
        type: "phase_marker",
        phase: `Phase ${phaseMatch[1]}${phaseMatch[2] ? `: ${phaseMatch[2].trim()}` : ""}`,
      });
      i++;
      continue;
    }

    // Markdown headings
    const headingMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (headingMatch) {
      flushProse();
      segments.push({
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      i++;
      continue;
    }

    // List items
    const listMatch = line.match(/^\s*(?:[-*]|\d+[.)]) (.+)/);
    if (listMatch) {
      flushProse();
      segments.push({ type: "list_item", text: listMatch[1] });
      i++;
      continue;
    }

    proseBuffer.push(line);
    i++;
  }

  flushProse();
  return segments;
}
